/* Ready Cart BD — cart quantity and delivery-area updates. */

(() => {
  const root = document.querySelector('[data-rcb-cart]');
  if (!root) return;

  /* Quantity and delivery updates use Shopify's rendered section so all money
     keeps Liquid formatting. Replace only cart regions; preserve the customer's
     order form and everything already typed into it. */
  const section = root.dataset.rcbSection;

  const REGIONS = ['.rcbk-items', '.rcbk-sum', '.rcbk-count'];

  /* Read straight off the section root by the pixel wiring, so a stale value
     here is a wrong `InitiateCheckout`. All move together: removing a
     line changes the content ids just as surely as it changes the value. */
  const PIXEL_HOOKS = ['rcbValue', 'rcbSubtotal', 'rcbItemCount', 'rcbContentIds', 'rcbContents', 'rcbDeliveryZone'];

  const busy = new WeakSet();

  const cartWrite = async (endpoint, body) => {
    /* ⚠ Not optional, and its absence fails silently: Shopify answers 200 with
       `sections: { <id>: null }`, the repaint finds nothing to paste, and the
       page sits there showing the cart as it was before the tap.

       Unlike the product page's writes this needs no `sections_url`. That one
       carries page context a cart POST does not have — `product` goes nil and
       the buy box renders empty — whereas this section reads only the cart.
       Verified against the live store: 27KB of section back from
       /cart/add.js with `sections` alone. */
    if (section) body.sections = section;

    root.dataset.rcbCartBusy = 'true';
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
      if (!response.ok) throw new Error(endpoint + ' failed');
      return await response.json();
    } finally { delete root.dataset.rcbCartBusy; }
  };

  /* This section as Shopify has just re-rendered it — from the cart itself,
     by Liquid, with the store's own money filter. Nothing here authors a line
     or a total, which is what keeps this page from becoming a second cart. */
  const rendered = (data) => {
    const html = section && data.sections ? data.sections[section] : null;
    if (!html) return null;

    return new DOMParser().parseFromString(html, 'text/html').querySelector('[data-rcb-cart]');
  };

  /* The spend-tier bar is redrawn with the bill. Its width and step are plain
     numbers Liquid put on it — read before the swap so the new bar can slide
     from where the old one stood, and shake only when a tap earned a tier. */
  const tierState = () => {
    const bar = root.querySelector('[data-rcb-tier]');
    return bar ? { bar, step: Number(bar.dataset.rcbTierStep), fill: Number(bar.dataset.rcbTierFill) } : null;
  };

  const animateTier = (before) => {
    const after = tierState();
    if (!before || !after) return;
    const fill = after.bar.querySelector('.rcbk-tier__fill');
    if (fill && before.fill !== after.fill) {
      fill.style.width = before.fill + '%';
      void fill.offsetWidth;
      fill.style.width = after.fill + '%';
    }
    if (after.step > before.step) after.bar.classList.add('is-unlocked');
  };

  /* The first time the bar is seen: fill from empty, then one shake, so a
     visitor notices there is a discount to reach. Once per page view, and
     not at all once the top tier is earned — there is nothing left to reach. */
  const nudgeTier = () => {
    const bar = root.querySelector('[data-rcb-tier]');
    if (!bar || bar.classList.contains('is-top') || !('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const fill = bar.querySelector('.rcbk-tier__fill');
    if (fill) {
      // Emptied without its transition, so the bar does not visibly drain first.
      fill.style.transition = 'none';
      fill.style.width = '0%';
      void fill.offsetWidth;
      fill.style.transition = '';
    }
    const seen = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      seen.disconnect();
      const current = root.querySelector('[data-rcb-tier]');
      if (current !== bar) return;
      if (fill) {
        void fill.offsetWidth;
        fill.style.width = bar.dataset.rcbTierFill + '%';
      }
      setTimeout(() => bar.classList.add('is-unlocked'), 450);
    }, { threshold: 0.6 });
    seen.observe(bar);
  };
  nudgeTier();

  const repaint = (fresh) => {
    const tierBefore = tierState();
    REGIONS.forEach((selector) => {
      const from = fresh.querySelector(selector);
      const to = root.querySelector(selector);
      if (from && to) to.innerHTML = from.innerHTML;
    });
    animateTier(tierBefore);

    PIXEL_HOOKS.forEach((key) => {
      if (fresh.dataset[key] !== undefined) root.dataset[key] = fresh.dataset[key];
    });

    /* -----------------------------------------------------------------------
       THE ORDER BUTTON'S TOTAL, WHICH IS NOT IN A SWAPPED REGION.

       ⚠ The order form is deliberately NOT repainted — a repaint would throw
       away whatever the customer has already typed into it, which on the last
       screen before a cash-on-delivery order is the most expensive thing this
       file could do. So its button's total would go stale the moment a stepper
       is tapped, showing yesterday's number on the control that commits to it.

       ⚠ THE FRESH STRING IS COPIED, NOT COMPUTED. It is the same figure the
       bill states, already rendered by Liquid's `money` filter and the store's
       own currency format. This theme formats no money in JavaScript anywhere,
       and the reason is exactly this kind of second opinion: the old order card
       totalled the cart itself and printed a different number from the form one
       screen below it.
       -------------------------------------------------------------------- */
    const freshTotal = fresh.querySelector('.rcbk-sum__amt strong');
    const shownTotal = root.querySelector('[data-rcb-order-total]');
    if (freshTotal && shownTotal) shownTotal.textContent = freshTotal.textContent.trim();

    /* The header badge is Horizon's and lives outside this section, rendered
       once at page load — so a cart written in place leaves it showing an
       earlier count. Horizon's `cart-icon.js` listens for
       `shopify:cart:lines-update`, so we speak its protocol rather than
       reaching into its DOM.

       `rcb-product.js` has to ask `/cart.js` for the number, because an add
       response describes the line it just added rather than the cart. Here the
       freshly rendered section already carries `cart.item_count`, so the badge
       costs no extra request. */
    const count = Number(fresh.dataset.rcbItemCount);
    if (!Number.isFinite(count)) return;

    const event = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
    event.promise = Promise.resolve({ cart: null, detail: { itemCount: count } });
    document.dispatchEvent(event);
  };

  root.addEventListener('change', async (event) => {
    const choice = event.target.closest('[data-rcb-delivery-choice]');
    if (!choice || !choice.checked) return;
    if ((root.dataset.rcbCartBusy === 'true' || root.dataset.rcbOrderBusy === 'true')) {
      root.querySelectorAll('[data-rcb-delivery-choice]').forEach((radio) => {
        radio.checked = radio.value === root.dataset.rcbDeliveryZone;
      });
      return;
    }
    const restoreFocus = choice.matches(':focus-visible');
    choice.closest('[data-rcb-delivery]').disabled = true;
    try {
      const data = await cartWrite('/cart/update.js', { attributes: { rcb_delivery_zone: choice.value } });
      const fresh = rendered(data);
      if (!fresh) throw new Error('Delivery total was not rendered');
      repaint(fresh);
      // Restore keyboard navigation without taking focus from a customer field.
      if (restoreFocus && (document.activeElement === document.body || document.activeElement === choice)) {
        root.querySelector('[data-rcb-delivery-choice]:checked')?.focus({ preventScroll: true });
      }
    } catch {
      // Re-read the persisted choice after an uncertain write; never keep a stale bill.
      window.location.reload();
    }
  });

  const applyChange = async (link) => {
    const row = link.closest('.rcbk-item');
    if (!row || busy.has(row)) return false;

    busy.add(row);
    row.classList.add('is-busy');

    try {
      const data = await cartWrite('/cart/change.js', {
        line: Number(link.dataset.rcbLine),
        quantity: Number(link.dataset.rcbTo),
      });

      const fresh = rendered(data);
      if (!fresh) throw new Error('section markup not found');

      /* Last line removed: the empty state is a different shape entirely, so
         let the browser render it rather than trying to morph into it. */
      if (fresh.querySelector('.rcbk-empty')) {
        window.location.assign(link.href);
        return true;
      }

      repaint(fresh);

      return true;
    } catch (error) {
      /* Anything at all went wrong — network, rate limit, markup we did not
         expect. Fall back to the link the browser was going to follow anyway,
         so a failed enhancement costs a page load and never the change. */
      window.location.assign(link.href);
      return false;
    } finally {
      busy.delete(row);
      row.classList.remove('is-busy');
    }
  };

  /* Delegated, because the rows themselves are replaced on every update. */
  root.addEventListener('click', (event) => {
    const link = event.target.closest('[data-rcb-qty]');
    if (!link || !root.contains(link)) return;
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    event.preventDefault();
    if ((root.dataset.rcbCartBusy === 'true' || root.dataset.rcbOrderBusy === 'true')) return;
    applyChange(link);
  });

})();
